import {
    keepPreviousData,
    useInfiniteQuery,
    useQuery,
} from "@tanstack/react-query";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import {
    type ParsedProviderListingsPageInput,
    type ProviderListingsPageInput,
    providerListingsPageInputContract,
    providerListingsQueryContract,
} from "@wags/catalog/listings/provider-listings-page.contract.ts";
import { getCatalog } from "@/lib/core/catalog-core.server";
import {
    ProviderListingReferencesPageSchema,
    type ProviderListingReferencesPage,
} from "./provider.contract";
import { toProviderListingReference } from "./provider-listing-reference";

export const PROVIDER_LISTINGS_PAGE_LIMIT = 40;
const PROVIDER_LISTINGS_CACHE_PAGES = 5;
const PROVIDER_LISTINGS_INITIAL_LIMIT = 9;

export const getProviderListingReferencesData = createServerOnlyFn(
    async (
        data: ParsedProviderListingsPageInput,
    ): Promise<ProviderListingReferencesPage> => {
        const catalog = await getCatalog();
        const page = await catalog.listings.getProviderListingsPage(data);
        return ProviderListingReferencesPageSchema.parse({
            listingReferences: page.listings.map(toProviderListingReference),
            recentlyAdoptedListingReferences: page.recentlyAdopted.map(
                toProviderListingReference,
            ),
            page: page.page,
            limit: page.limit,
            total: page.total,
            availableCount: page.availableCount,
            nextPage: page.nextPage,
        });
    },
);

const getProviderListingReferences = createServerFn({ method: "GET" })
    .inputValidator((data: ProviderListingsPageInput) =>
        providerListingsPageInputContract.parse(data),
    )
    .handler(async ({ data }) => getProviderListingReferencesData(data));

export function getProviderListingReferencesInitialQueryOptions(
    providerId: string | undefined,
) {
    return {
        enabled: Boolean(providerId),
        staleTime: 30_000,
        queryKey: [
            "provider-listing-references-initial",
            providerId,
            PROVIDER_LISTINGS_INITIAL_LIMIT,
        ],
        queryFn: async () => {
            if (!providerId) throw new Error("Provider ID is required.");
            return getProviderListingReferences({
                data: {
                    providerId,
                    page: 0,
                    limit: PROVIDER_LISTINGS_INITIAL_LIMIT,
                    includeRecentlyAdopted: true,
                },
            });
        },
    };
}

export function useProviderListingReferencesInitialPage(
    providerId: string | undefined,
) {
    return useQuery(
        getProviderListingReferencesInitialQueryOptions(providerId),
    );
}

type ProviderListingsPageParam = ProviderListingsPageInput &
    Required<Pick<ProviderListingsPageInput, "page" | "limit">>;

export const providerListingReferencesQueryKeyPrefix = (
    providerId: string | undefined,
): readonly ["provider-listing-references", string | undefined] => [
    "provider-listing-references",
    providerId,
];

export function useInfiniteProviderListingReferences(
    providerId: string | undefined,
    enabled: boolean,
    query?: string,
) {
    const normalizedQuery =
        providerListingsQueryContract.parse(query ?? "") || undefined;
    return useInfiniteQuery({
        enabled: Boolean(providerId) && enabled,
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        queryKey: [
            ...providerListingReferencesQueryKeyPrefix(providerId),
            PROVIDER_LISTINGS_PAGE_LIMIT,
            normalizedQuery,
        ],
        queryFn: ({ pageParam }) =>
            getProviderListingReferences({ data: pageParam }),
        initialPageParam: {
            providerId: providerId ?? "",
            page: 0,
            limit: PROVIDER_LISTINGS_PAGE_LIMIT,
            query: normalizedQuery,
            includeRecentlyAdopted: false,
        } satisfies ProviderListingsPageParam,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
            lastPage.listingReferences.length === 0 || !lastPage.nextPage
                ? undefined
                : { ...lastPageParam, page: lastPage.nextPage },
        getPreviousPageParam: (firstPage, _allPages, firstPageParam) =>
            firstPage.page <= 0
                ? undefined
                : { ...firstPageParam, page: firstPage.page - 1 },
        maxPages: PROVIDER_LISTINGS_CACHE_PAGES,
    });
}
